import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';

import {
  getAgileIssue,
  getGanttAddBasePlan,
  getIssueData,
  getIssuePermissions,
  saveIssueEdit,
} from '../../../../packages/common/api';
import FormField from '../../../../packages/common/shared-ui/src/components/FormField/FormField';
import WarningModal from '../../../../packages/common/shared-ui/src/components/WarningModal';
import ClearIcon from '../../../../packages/common/shared-ui/src/icons/ClearIcon';
import CopyIcon from '../../../../packages/common/shared-ui/src/icons/CopyIcon';
import DeleteIssueIconBig from '../../../../packages/common/shared-ui/src/icons/DeleteIssueIconBig';
import {
  formatFields,
  saveFormat,
} from '../../../../packages/common/utils/fieldHelper';
import { formValidation } from '../../../../packages/common/utils/formValidation';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';

// Original class: agileIssuePaper
const StyledAgileIssuePaper = styled('div')(theme => ({
  '& .MuiDrawer-paper': {
    height: 'auto',
    minHeight: '300px',
    maxHeight: 'calc(100vh - 100px)',
    marginTop: '80px',
    right: '20px',
    border: '1px solid #E4E4EF',
    borderRadius: '16px',
    boxShadow: '0px 5px 10px -8px #00000033',
    width: '394px',
    padding: '16px',
  },
}));
// Original class: scrollContainer
const StyledScrollContainer = styled(Grid)(theme => ({
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#F1F2F8',
    marginRight: '2px',
    marginTop: '10px',
    marginBottom: '10px',
  },

  '&::-webkit-scrollbar': {
    width: '4px',
    backgroundColor: 'white !important',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#7174AC',
    borderRadius: '5px',
  },
}));
const AgileIssue = ({ open, issue, close, handleSave, action, indicators }) => {
  const IssueAction = { COPY: 'copy', DELETE: 'delete' };
  const getLocalString = useTranslation();
  const pageParams = useParams();
  const navigate = useNavigate();
  const [activeIssue, setActiveIssue] = useState(null);
  const [issueAllFields, setIssueAllFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [issueTracker, setIssueTracker] = useState(null);
  const [initialData, setInitialData] = useState([]);
  const [formChanged, setFormChanged] = useState(false);
  const [errors, setErrors] = useState([]);
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [permissions, setPermissions] = useState(null);

  const handleError = useCallback(
    error => {
      if (error) {
        navigate(`/errors/error-${error.status}`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    issue && setActiveIssue(issue);
  }, [issue]);

  useEffect(() => {
    let ignore = false;
    async function getPermissions() {
      const { data: res } = await getIssuePermissions(activeIssue.issueId);
      if (!ignore) {
        return res;
      }
    }
    if (activeIssue) {
      getPermissions()
        .then(permission => {
          setPermissions(permission);
        })
        .catch(error => handleError(error.response));
      return () => {
        ignore = true;
      };
    }
  }, [handleError, activeIssue]);

  const getForm = useCallback(async () => {
    if (!activeIssue) {
      return;
    }
    let params = {};
    pageParams.projectId && (params.project = pageParams.projectId);
    indicators !== undefined && (params.indicators = indicators);
    try {
      const response = await getAgileIssue(params, activeIssue.issueId);
      if (response) {
        const { data: issueData } = response;
        let fields = {};
        issueData.issue.standards &&
          (fields.standards = issueData.issue.standards);
        issueData.issue.customs && (fields.customs = issueData.issue.customs);
        const formattedFields = formatFields(fields);
        setIssueAllFields(formattedFields);
        setInitialData(formattedFields);
        issueData.issue.hierarchy &&
          issueData.issue.hierarchy[0].trackerName &&
          setIssueTracker(issueData.issue.hierarchy[0].trackerName);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      handleError(error.response);
    }
  }, [activeIssue, handleError, indicators, pageParams.projectId]);

  useEffect(() => {
    getForm();
  }, [getForm]);

  const handleFormField = useCallback((data, field) => {
    setIssueAllFields(prevFields => {
      const pos = prevFields.map(e => e.fieldId).indexOf(field.fieldId);
      const fieldValue = {
        ...field,
        ...data,
      };
      return [
        ...prevFields.slice(0, pos),
        fieldValue,
        ...prevFields.slice(pos + 1),
      ];
    });
    setFormChanged(true);
  }, []);

  const handleValidation = useCallback(() => {
    const requiredFields = ['subject', 'priority', 'statusId'];
    const fieldsToValidate = issueAllFields.filter(item =>
      requiredFields.includes(item.fieldId),
    );
    const validationResult = formValidation(fieldsToValidate, null, true);
    setIssueAllFields([
      ...validationResult.fields,
      ...issueAllFields.filter(item => !requiredFields.includes(item.fieldId)),
    ]);
    setErrors(validationResult.errors);
    return validationResult.error;
  }, [issueAllFields]);

  const handleSaveButton = useCallback(async () => {
    setSaveLoading(true);
    const validation = handleValidation();
    if (validation) {
      setSaveLoading(false);
      return;
    }
    const data = saveFormat({
      standards: issueAllFields.filter(item => !item.custom),
      customs: issueAllFields.filter(item => item.custom),
    });
    try {
      const response = await saveIssueEdit(activeIssue.issueId, data);
      if (response) {
        const { data: issue } = response;
        setInitialData(issueAllFields);
        setSaveLoading(false);
        setOpenWarningModal(false);
        handleSave(issue.id, issueAllFields);
        setFormChanged(false);
        await getForm();
      }
    } catch (error) {
      setSaveLoading(false);
      handleError(error.response);
    }
    //handleSave();
  }, [
    handleValidation,
    issueAllFields,
    activeIssue,
    handleSave,
    getForm,
    handleError,
  ]);
  const handleCloseButton = useCallback(() => {
    close();
  }, [close]);

  const handleModalAction = useCallback(
    async e => {
      switch (e.currentTarget.attributes.value.value) {
        case 'save':
          await handleSaveButton();
          handleCloseButton();
          break;
        case 'delete':
          setOpenWarningModal(false);
          handleCloseButton();
          break;
      }
    },
    [handleCloseButton, handleSaveButton],
  );

  const handleWarningModal = useCallback(() => {
    if (formChanged) {
      setOpenWarningModal(true);
    } else {
      handleCloseButton();
    }
  }, [formChanged, handleCloseButton]);

  const handleClearButton = useCallback(() => {
    setIssueAllFields(initialData);
    setFormChanged(false);
  }, [initialData]);

  return (
    <Drawer
      elevation={0}
      hideBackdrop={true}
      anchor={'right'}
      open={open}
      slotProps={{
        root: { component: StyledAgileIssuePaper }, // заменяем root Modal на styled-компонент
      }}
    >
      <WarningModal
        issue
        simple
        isWarningModalOpen={openWarningModal}
        handleToggle={handleModalAction}
        closeWarningModal={() => setOpenWarningModal(false)}
        title={getLocalString('messages', 'saveChanges')}
      />
      {(loading || saveLoading) && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 11,
            backgroundColor: loading ? '#fff' : 'transparent',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      )}
      <Grid
        container
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <Grid>
          {permissions && permissions.canDelete && (
            <IconButton
              sx={{ padding: 0 }}
              onClick={() => action(IssueAction.DELETE)}
            >
              <DeleteIssueIconBig />
            </IconButton>
          )}
          {permissions && permissions.canCopy && (
            <IconButton
              sx={{ padding: 0, marginLeft: '4px' }}
              onClick={() => action(IssueAction.COPY)}
            >
              <CopyIcon width={20} height={20} viewBox='0 0 24 24' />
            </IconButton>
          )}
        </Grid>
        <Grid>
          <IconButton
            disableRipple
            value='close'
            onClick={handleWarningModal}
            sx={{ padding: 0 }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
              }}
            >
              <ClearIcon width={24} height={24} viewBox='0 0 24 24' />
            </Box>
          </IconButton>
        </Grid>
      </Grid>
      <StyledScrollContainer
        container
        sx={{
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Link
          to={
            pageParams.projectId
              ? `/projects/${pageParams.projectId}/issues/${activeIssue?.issueId}`
              : `/issues/${activeIssue?.issueId}`
          }
          target={'_blank'}
          sx={{ display: 'inline-flex' }}
        >
          <Typography
            variant='h4'
            sx={{
              fontWeight: 600,
              color: '#6270D0',
              textDecoration: 'underline',
            }}
          >
            {`${issueTracker} #${activeIssue?.issueId}`}
          </Typography>
        </Link>
        {errors.length > 0 && (
          <Box>
            {errors.map((error, index) => (
              <Typography
                key={index}
                variant='h3'
                sx={{
                  color: '#E03737',
                }}
              >
                {getLocalString('errors', error.message)}
              </Typography>
            ))}
          </Box>
        )}
        {issueAllFields.map((field, index) => (
          <Box sx={{ width: '100%' }} key={index}>
            <FormField
              smallLabel={true}
              key={index}
              field={field}
              fieldEvent={handleFormField}
            ></FormField>
          </Box>
        ))}
      </StyledScrollContainer>
      <Grid
        container
        sx={{
          justifyContent: 'center',
          marginTop: 5,
          marginBottom: '4px',
        }}
      >
        <Button
          disableRipple
          className={clsx('defaultButton', 'secondary cutRight')}
          onClick={handleClearButton}
          disabled={!formChanged}
        >
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {getLocalString('buttons', 'cancel')}
          </Typography>
        </Button>
        <Box
          sx={{
            marginLeft: '5px',
          }}
        >
          <Button
            disableRipple
            className={clsx('defaultButton', 'primary cutLeft')}
            onClick={handleSaveButton}
            disabled={!formChanged}
          >
            <Typography
              variant='h4'
              sx={{
                fontWeight: 700,
              }}
            >
              {getLocalString('buttons', 'save')}
            </Typography>
          </Button>
        </Box>
      </Grid>
    </Drawer>
  );
};

export default AgileIssue;
